import {
  startOfMonth,
  isSameMonth,
  startOfWeek,
  endOfWeek,
  endOfMonth,
  isFirstDayOfMonth,
} from "date-fns";
import { useAtomValue } from "jotai";
import React from "react";

import { VStack } from "@whoosh/design-system/src/components/primitive";

import { getCalendarSettingsForDateAtom } from "../atoms";
import { CalendarProps } from "../types";
import { getFollowingMonth } from "../utils/calendarDateUtils";
import { CalendarBody } from "./CalendarBody";
import { DaysOfWeekHeader } from "./DaysOfWeekHeader";
import { MonthNameHeader } from "./MonthNameHeader";

export const Calendar = ({
  endDate,
  isLeft,
  isRight,
  monthDateObj,
  startDate,
  showDayOfWeekHeader,
  showMonthNameHeader = true,
  fillEmptyDays,
  clubWeekStartsOn,
}: CalendarProps) => {
  const nextMonthObj = getFollowingMonth(monthDateObj);
  const firstDayOfNextMonth = startOfMonth(nextMonthObj);
  const getCalendarSettingsForDate = useAtomValue(
    getCalendarSettingsForDateAtom
  );
  const { showDate: showNextMonth } = getCalendarSettingsForDate(
    firstDayOfNextMonth,
    monthDateObj
  );

  // calculates date ranges that this calendar month will show in vertical mode
  const getVerticalCalendarDateRange = () => {
    return fillEmptyDays
      ? [
          startOfWeek(startDate, { weekStartsOn: clubWeekStartsOn }),
          endOfWeek(endDate, { weekStartsOn: clubWeekStartsOn }),
        ]
      : [startDate, endDate];
  };

  const [firstVisibleDate, lastVisibleDate] = getVerticalCalendarDateRange();

  const isPartOfMultipleMonthDisplay = isRight || isLeft;

  const showNextMonthBelow =
    showNextMonth &&
    !isPartOfMultipleMonthDisplay &&
    !isFirstDayOfMonth(startDate) &&
    !isSameMonth(lastVisibleDate, monthDateObj);
  return (
    <VStack
      minWidth={300}
      flexGrow={1}
      flexBasis={300}
      pl={!!isRight ? 2 : 0}
      pr={!!isLeft ? 2 : 0}
    >
      <VStack gap={2}>
        {showMonthNameHeader ? (
          <MonthNameHeader
            monthDateObj={monthDateObj}
            clubWeekStartsOn={clubWeekStartsOn}
          />
        ) : null}
        {showDayOfWeekHeader ? (
          <DaysOfWeekHeader clubWeekStartsOn={clubWeekStartsOn} />
        ) : null}

        <CalendarBody
          monthDateObj={monthDateObj}
          startDate={firstVisibleDate}
          endDate={
            lastVisibleDate > endOfMonth(monthDateObj)
              ? endOfMonth(monthDateObj)
              : lastVisibleDate
          }
          clubWeekStartsOn={clubWeekStartsOn}
        />
      </VStack>

      {showNextMonthBelow ? (
        <VStack paddingTop={4} gap={2}>
          <MonthNameHeader
            monthDateObj={nextMonthObj}
            clubWeekStartsOn={clubWeekStartsOn}
          />
          <CalendarBody
            monthDateObj={nextMonthObj}
            startDate={startOfMonth(nextMonthObj)}
            endDate={
              lastVisibleDate > endOfMonth(nextMonthObj)
                ? endOfMonth(nextMonthObj)
                : lastVisibleDate
            }
            clubWeekStartsOn={clubWeekStartsOn}
          />
        </VStack>
      ) : null}
    </VStack>
  );
};
